import React, { useEffect, useState } from 'react';
import TooltipIcon from '@assets/icons/tooltip-icon.svg';
import { BaseButton, BaseSelect, BaseTooltip } from '@components';
import { InputLabel, InputWrapper } from '@components/input/styled.tsx';
import { ALERT_STATUSES, AlertNotifications } from '@constants/alertNotifications.ts';
import { INTERNAL_SERVER_ERROR_STATUS_CODE } from '@constants/api.ts';
import { ROOT_TOKENS_COLORS } from '@constants/colors.ts';
import { TEST_IDS } from '@constants/testIds.ts';
import { DATE_FORMAT_PREFERENCE, START_PAGE_PREFERENCE, TIME_FORMAT_PREFERENCE } from '@constants/userPreferences.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useNotify } from '@hooks/useNotify.ts';
import { useResponsive } from '@hooks/useResponsive.ts';
import { useUserRoles } from '@hooks/useUserRoles.ts';
import { useLingui } from '@lingui/react/macro';
import { TooltipContent, TooltipContentWrapper } from '@routes/___shared___/styled.tsx';
import { getFullUserInfo } from '@routes/___shared___/user.slice.ts';
import { getUserPreferencesOrDefaults } from '@routes/___shared___/utils.ts';
import {
    TabContentContainer as Container,
    TabContentInfoHeader as Header,
    TabContentInfoText as InfoText,
    TabContentInfoWrapper as InfoWrapper,
    TabContentWrapper as Wrapper,
} from '@routes/profile-bar/___shared___/styled.tsx';
import { updateUserPreferences } from '@routes/sidebar/settings/model/settings.slice.ts';
import {
    SHARED_USER_PREFERENCES_TAB_DATE_FORMAT_LABEL,
    SHARED_USER_PREFERENCES_TAB_HEADER,
    SHARED_USER_PREFERENCES_TAB_INFO,
    SHARED_USER_PREFERENCES_TAB_SAVE,
    SHARED_USER_PREFERENCES_TAB_START_PAGE_LABEL,
    SHARED_USER_PREFERENCES_TAB_TIME_FORMAT_LABEL,
    SHARED_USER_PREFERENCES_TAB_TOAST_DESCRIPTION,
    SHARED_USER_PREFERENCES_TAB_UNDO,
} from '@routes/sidebar/settings/ui/user-preferences-tab/constants.ts';
import { clearAlert, setAlert } from '@shared/model/alert.slice.ts';
import { persistPreferredStartPage } from '@store/services/localStorage.service.ts';
import type { FormField, UserPreferencesSettings } from '@types';
import { getDateFormatOptions } from '@utils/date.ts';
import { renderAlertErrorDesc } from '@utils/general.tsx';
import { filterStartPageOptionsByUserAccess } from '@utils/roles.ts';
import { showToast, TOAST_TYPE } from '@utils/toast.tsx';
import { translateItem } from '@utils/translations.ts';
import { Form } from 'antd';

import { getStartPagesOptions, getTimeFormatOptions } from './constants.ts';
import { ButtonWrapper, DateTimeWrapper, StartPageWrapper } from './styled.tsx';

const hasFormValuesChanged = (formFields: FormField[], initialFormValues: UserPreferencesSettings): boolean => {
    for (const field of formFields) {
        const [fieldName] = field.name;
        const currentValue = field.value;
        const initialValue = initialFormValues[fieldName as keyof UserPreferencesSettings];

        if (typeof currentValue === 'undefined') continue;

        if (currentValue !== initialValue) {
            return true;
        }
    }

    return false;
};

const UserPreferences: React.FC = () => {
    const { t } = useLingui();
    const notify = useNotify();
    const userRoles = useUserRoles();
    const userInfo = useAppSelector((state) => state.user.userInfo);
    const [initialFormValues, setInitialFormValues] = useState(getUserPreferencesOrDefaults(userInfo));
    const USER_PREFERENCES_TOOLTIP_TEXT = t`Any changes made in preferences will be reflected after user log off and login again`;
    // BBL-2109 const PREFERRED_COMMUNICATION_TOOLTIP_TEXT = t`Some notifications are mandatory and can not be turn off`;
    const TOAST_DESCRIPTION = translateItem(SHARED_USER_PREFERENCES_TAB_TOAST_DESCRIPTION);

    const [formFields, setFormFields] = useState<FormField[]>([]);
    const [hasFormChanged, setHasFormChanged] = useState<boolean>(hasFormValuesChanged(formFields, initialFormValues));

    const { isBigScreen } = useResponsive();
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();

    const onFieldsChange = (_: any[], allFields: any[]) => {
        setHasFormChanged(hasFormValuesChanged(allFields as FormField[], initialFormValues));
        setFormFields(allFields as FormField[]);
    };

    const handleSubmit = () =>
        form.validateFields().then((data) => {
            showToast(notify, null, TOAST_DESCRIPTION, TOAST_TYPE.INFO);

            dispatch(updateUserPreferences(data))
                .then(() => {
                    persistPreferredStartPage(data[START_PAGE_PREFERENCE]);
                    void dispatch(getFullUserInfo());
                })
                .catch((err) => {
                    if (err.status < INTERNAL_SERVER_ERROR_STATUS_CODE) {
                        dispatch(clearAlert()); //skip banner
                    } else {
                        dispatch(
                            setAlert({
                                type: ALERT_STATUSES.ERROR,
                                message: AlertNotifications[ALERT_STATUSES.ERROR].message,
                                description: renderAlertErrorDesc(err.status, err.statusText),
                            }),
                        );
                    }
                });
        });

    useEffect(() => {
        if (userInfo) {
            const values = getUserPreferencesOrDefaults(userInfo);
            setInitialFormValues(values);
            form.setFieldsValue(values);
        }
    }, [userInfo, form]);

    return (
        <Form form={form} onFinish={handleSubmit} onFieldsChange={onFieldsChange} initialValues={initialFormValues}>
            <Wrapper $hasData>
                <Container $isBigScreen={isBigScreen}>
                    <InfoWrapper>
                        <TooltipContentWrapper>
                            <Header>{translateItem(SHARED_USER_PREFERENCES_TAB_HEADER)}</Header>
                            <BaseTooltip
                                arrow={false}
                                title={<TooltipContent>{USER_PREFERENCES_TOOLTIP_TEXT}</TooltipContent>}
                                placement="right"
                                color={ROOT_TOKENS_COLORS.white.default}
                                icon={<TooltipIcon />}
                            />
                        </TooltipContentWrapper>
                        <InfoText>{translateItem(SHARED_USER_PREFERENCES_TAB_INFO)}</InfoText>
                    </InfoWrapper>
                    <StartPageWrapper>
                        <InputWrapper>
                            <InputLabel>{translateItem(SHARED_USER_PREFERENCES_TAB_START_PAGE_LABEL)}</InputLabel>
                            <Form.Item noStyle name={START_PAGE_PREFERENCE}>
                                <BaseSelect
                                    data-cy={TEST_IDS.SETTINGS.START_PAGE_SELECT}
                                    options={filterStartPageOptionsByUserAccess(
                                        userInfo,
                                        getStartPagesOptions(),
                                        userRoles,
                                    )}
                                />
                            </Form.Item>
                        </InputWrapper>
                    </StartPageWrapper>
                    <DateTimeWrapper>
                        <InputWrapper>
                            <InputLabel>{translateItem(SHARED_USER_PREFERENCES_TAB_DATE_FORMAT_LABEL)}</InputLabel>
                            <Form.Item noStyle name={DATE_FORMAT_PREFERENCE}>
                                <BaseSelect
                                    data-cy={TEST_IDS.SETTINGS.DATE_FORMAT_SELECT}
                                    options={getDateFormatOptions()}
                                />
                            </Form.Item>
                        </InputWrapper>
                        <InputWrapper>
                            <InputLabel>{translateItem(SHARED_USER_PREFERENCES_TAB_TIME_FORMAT_LABEL)}</InputLabel>
                            <Form.Item noStyle name={TIME_FORMAT_PREFERENCE}>
                                <BaseSelect
                                    data-cy={TEST_IDS.SETTINGS.TIME_FORMAT_SELECT}
                                    options={getTimeFormatOptions()}
                                />
                            </Form.Item>
                        </InputWrapper>
                    </DateTimeWrapper>
                    {/* BBL-2109 */}
                    {/*<InputWrapper>*/}
                    {/*    <TooltipContentWrapper>*/}
                    {/*        <InputLabel>{t`Preferred communication`}</InputLabel>*/}
                    {/*        <BaseTooltip*/}
                    {/*            arrow={false}*/}
                    {/*            title={<TooltipContent>{PREFERRED_COMMUNICATION_TOOLTIP_TEXT}</TooltipContent>}*/}
                    {/*            placement="right"*/}
                    {/*            color={ROOT_TOKENS_COLORS.white.default}*/}
                    {/*            icon={*/}
                    {/*                <TooltipIconWrapper>*/}
                    {/*                    <TooltipIcon />*/}
                    {/*                </TooltipIconWrapper>*/}
                    {/*            }*/}
                    {/*        />*/}
                    {/*    </TooltipContentWrapper>*/}
                    {/*    <SwitchesWrapper>*/}
                    {/*        <SwitchItem>*/}
                    {/*            <Form.Item noStyle name={APP_NOTIFICATION_PREFERENCE}>*/}
                    {/*                <BaseSwitch data-cy={TEST_IDS.SETTINGS.SWITCH_APP_NOTIFICATIONS} />*/}
                    {/*            </Form.Item>*/}
                    {/*            <SwitchLabel>{t`OK for app notifications`}</SwitchLabel>*/}
                    {/*        </SwitchItem>*/}
                    {/*        <SwitchItem>*/}
                    {/*            <Form.Item noStyle name={EMAIL_NOTIFICATION_PREFERENCE}>*/}
                    {/*                <BaseSwitch />*/}
                    {/*            </Form.Item>*/}
                    {/*            <SwitchLabel>{t`OK for email notifications`}</SwitchLabel>*/}
                    {/*        </SwitchItem>*/}
                    {/*    </SwitchesWrapper>*/}
                    {/*</InputWrapper>*/}
                </Container>
            </Wrapper>
            {!!hasFormChanged && (
                <ButtonWrapper>
                    <Form.Item noStyle>
                        <BaseButton htmlType="submit">{translateItem(SHARED_USER_PREFERENCES_TAB_SAVE)}</BaseButton>
                    </Form.Item>
                    <Form.Item noStyle>
                        <BaseButton
                            $customType="outline"
                            onClick={() => {
                                form.resetFields();
                            }}
                        >
                            {translateItem(SHARED_USER_PREFERENCES_TAB_UNDO)}
                        </BaseButton>
                    </Form.Item>
                </ButtonWrapper>
            )}
        </Form>
    );
};

export default UserPreferences;
